import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { inr, xx } from '@krit/core';
import { getPortfolio, getUpdates, addUpdate, getNews, refreshNewsApi, type PortfolioHolding } from '../lib/api.js';
import { Card, StatTile, PageHeader, Pill, ProgressBar } from '../components/ui.js';

const CAT_TONE: Record<string, string> = {
  Company: 'navy', Sector: 'teal', IPO: 'green', Market: 'purple', Regulatory: 'amber', Internal: 'gold', Milestone: 'gold',
};
const CATS = ['All', 'Company', 'Sector', 'IPO', 'Market', 'Regulatory'];

export function Portfolio() {
  const qc = useQueryClient();
  const { data: pf } = useQuery({ queryKey: ['portfolio'], queryFn: getPortfolio, retry: 2, retryDelay: 800 });
  const { data: upd } = useQuery({ queryKey: ['updates'], queryFn: getUpdates, retry: false });
  const { data: newsData } = useQuery({ queryKey: ['news'], queryFn: getNews, retry: false });

  const [cat, setCat] = useState('All');
  const [headline, setHeadline] = useState('');
  const [note, setNote] = useState('');

  const holdings = pf?.holdings ?? [];
  const r = pf?.rollups;
  const updates = upd?.updates ?? [];
  const news = (newsData?.news ?? []).filter((n) => cat === 'All' || n.category === cat);

  const add = useMutation({
    mutationFn: () => addUpdate({ headline: headline.trim(), note: note.trim() || undefined, category: 'Internal' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['updates'] }); setHeadline(''); setNote(''); },
  });

  const [refreshMsg, setRefreshMsg] = useState('');
  const refresh = useMutation({
    mutationFn: refreshNewsApi,
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['news'] }); setRefreshMsg(`+${res.inserted} new`); },
    onError: (e) => setRefreshMsg(String((e as Error).message)),
  });

  return (
    <div>
      <PageHeader eyebrow="Portfolio" title="Monitoring" desc={`${holdings.length} holdings · live news & market feed · internal updates`} />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <StatTile label="Holdings" value={r?.count ?? 0} note="invested companies" accent="teal" />
        <StatTile label="Invested" value={inr(r?.invested ?? 0)} note="deployed capital" accent="navy" />
        <StatTile label="Current mark" value={inr(r?.mark ?? 0)} note="fair value" accent="green" />
        <StatTile label="Blended MOIC" value={r?.blendedMOIC ? xx(r.blendedMOIC) : '—'} note="mark ÷ invested" accent="gold" />
        <StatTile label="Avg IPO readiness" value={`${r?.avgIpoReadiness ?? 0}%`} note="across book" accent="purple" />
      </div>

      {/* Holdings — full width, equal-height cards */}
      <div className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-2 mb-2.5">Holdings</div>
      {holdings.length === 0 ? (
        <Card className="mb-6"><div className="py-8 text-center text-muted">No holdings yet.</div></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6 items-stretch">
          {holdings.map((h) => <HoldingCard key={h.id} h={h} />)}
        </div>
      )}

      {/* News + internal updates — balanced columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <Card className="lg:col-span-2" bodyClass="p-0"
          title="News &amp; market feed"
          actions={<div className="flex gap-1.5 flex-wrap items-center">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${cat === c ? 'bg-navy text-white border-navy' : 'bg-white text-muted border-line hover:border-muted-2'}`}>{c}</button>
            ))}
            <button onClick={() => { setRefreshMsg(''); refresh.mutate(); }} disabled={refresh.isPending} title="Pull the latest headlines for the invested book"
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-teal text-teal-d bg-white hover:bg-teal/5 disabled:opacity-50">{refresh.isPending ? '↻ Refreshing…' : '↻ Refresh'}</button>
            {refreshMsg && <span className="text-[10.5px] text-muted-2">{refreshMsg}</span>}
          </div>}>
          <div className="max-h-[560px] overflow-y-auto divide-y divide-line-2">
            {news.map((n) => (
              <a key={n.id} href={n.url ?? '#'} target="_blank" rel="noreferrer" className="flex gap-3 px-5 py-3 hover:bg-canvas transition-colors">
                <Pill tone={CAT_TONE[n.category ?? ''] ?? 'gray'} className="self-start mt-0.5">{n.category}</Pill>
                <div className="min-w-0">
                  <div className="font-semibold text-navy text-[13px] leading-snug">{n.headline}</div>
                  <div className="text-[11px] text-muted-2 mt-0.5">{n.source} · {n.publishedAt} · {n.sector}</div>
                </div>
              </a>
            ))}
            {news.length === 0 && <div className="px-5 py-8 text-center text-muted text-[12.5px]">No items in this category.</div>}
          </div>
        </Card>

        <Card title="Internal updates" bodyClass="p-4">
          <div className="space-y-2 mb-3">
            <input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Update headline…" className="w-full px-3 py-2 border border-line rounded-lg text-[13px]" />
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)…" className="w-full px-3 py-2 border border-line rounded-lg text-[13px] min-h-[46px] resize-none" />
            <button disabled={!headline.trim() || add.isPending} onClick={() => add.mutate()} className="w-full px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50">{add.isPending ? 'Adding…' : '+ Add update'}</button>
          </div>
          <div className="max-h-[420px] overflow-y-auto -mx-1 px-1">
            {updates.length === 0 && <p className="text-[12px] text-muted text-center py-4">No internal updates yet.</p>}
            {updates.map((u) => (
              <div key={u.id} className="border-b border-line-2 py-2.5 last:border-0">
                <div className="flex gap-2 items-center"><Pill tone={CAT_TONE[u.category ?? 'Internal'] ?? 'gold'}>{u.category}</Pill>{u.date && <span className="text-[10.5px] text-muted-2">{u.date}</span>}</div>
                <div className="text-[12.5px] font-semibold text-navy mt-1">{u.headline}</div>
                {u.note && <div className="text-[11.5px] text-muted mt-0.5">{u.note}</div>}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function HoldingCard({ h }: { h: PortfolioHolding }) {
  const up = h.holding.mark >= h.holding.invested;
  return (
    <div className="flex flex-col h-full bg-card border border-line rounded-xl shadow-sh overflow-hidden transition-shadow hover:shadow-sh2">
      <div className="h-1 bg-gradient-to-r from-navy to-teal" />
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div>
            <Link to={`/deals/${h.id}`} className="font-extrabold text-navy text-[15px] hover:text-teal-d">{h.short}</Link>
            <div className="text-[11px] text-muted mt-0.5">{h.sector}</div>
          </div>
          <div className="text-right">
            <div className="text-[15px] font-extrabold text-navy mono leading-none">{h.baseMOIC ? xx(h.baseMOIC) : '—'}</div>
            <div className="text-[9px] uppercase tracking-[0.4px] text-muted-2 mt-0.5">Base MOIC</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4 py-3 border-y border-line-2 text-center">
          <div><div className="text-[9px] uppercase tracking-[0.3px] text-muted-2">Invested</div><div className="text-[13px] font-bold text-navy mono mt-0.5">{inr(h.holding.invested)}</div></div>
          <div><div className="text-[9px] uppercase tracking-[0.3px] text-muted-2">Mark</div><div className={`text-[13px] font-bold mono mt-0.5 ${up ? 'text-green' : 'text-navy'}`}>{inr(h.holding.mark)}</div></div>
          <div><div className="text-[9px] uppercase tracking-[0.3px] text-muted-2">Own</div><div className="text-[13px] font-bold text-navy mono mt-0.5">{h.holding.ownership}%</div></div>
        </div>

        <div className="mt-3">
          <div className="flex justify-between text-[10.5px] text-muted mb-1.5"><span>{h.holding.ipoStage}</span><span className="font-semibold text-navy">{h.holding.ipoPct}%</span></div>
          <ProgressBar pct={h.holding.ipoPct} tone="teal" />
        </div>

        <div className="mt-auto pt-3 text-[11.5px] text-muted leading-snug"><span className="font-semibold text-navy">Next:</span> {h.holding.milestone}</div>
      </div>
    </div>
  );
}
